<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" max-width="960" persistent fluid scrollable content-class="round-2xl")
  v-card
    v-card-title.pa-4
      v-row(no-gutters)
        v-col(cols="10" offset="1")
          .text-xl.font-bold.text-center Record new payment
        v-col(cols="1" align="right")
          v-btn(icon @click="closeDialog" small color="primary")
            v-icon mdi-close
    v-divider
    v-card-text.primary--text.pa-6
      template(v-if="isFetchingPaymentMethods || isFetchingClaimPanels")
        v-row(no-gutters)
          v-col(cols="7")
            v-skeleton-loader(type="article@3")
          v-col(cols="5")
            v-skeleton-loader(type="article@1")

      v-form(v-else ref="form")
        v-row
          v-col(cols="5")
            .left-panel
              //- amount due
              v-card.round-xl.neutral200.pa-3(flat)
                div.yz-medium-body-xs.text-neutral-500 Recorded payment
                template(v-if="!transactions.length")
                  div.text-xs.font-medium.neutral500--text.mt-2 No payment recorded
                div.mt-2(v-else)
                  template(v-for="(transaction, index) in transactions")
                    div.d-flex.align-center(:key="'txn-' + index")
                      div.text-xs.neutral700--text {{ transaction.payment_method }}
                      v-spacer
                      div.text-xs.neutral700--text {{ transaction.amount | displayPrice($currency) }}
                    v-divider.my-1

              v-card.round-xl.neutral200.pa-3.mt-2(v-if="showRemarks || showVerifyUrl || showVisitPanelNote || showStaffId" flat)
                div(style="display:grid; row-gap: 8px;")
                  //- verification link
                  div.d-flex.align-center(v-if="showVerifyUrl")
                    div.ellipsis-text-1.pr-2
                      div.yz-medium-body-xs.text-neutral-500 Verification link
                      div.yz-regular-body-xs.neutral700--text.ellipsis-text-1 {{ selectedPayment.verification_link }}
                    v-spacer
                    yz-btn(label="Open link" size="sm" @click="openVerificationLink(selectedPayment.verification_link)")

                  //- remarks
                  div(v-if="showRemarks")
                    div.yz-medium-body-xs.text-neutral-500 Remarks
                    div.yz-regular-body-sm.neutral700--text.mt-1 {{ selectedPayment.remarks }}

                  yz-divider.yz-neutral-300.my-1(v-if="(showVerifyUrl || showRemarks) && (showVisitPanelNote || showStaffId)")

                  //- staff id
                  div(v-if="showStaffId")
                    div.yz-medium-body-xs.text-neutral-500 Staff ID
                    div.yz-regular-body-sm.neutral700--text.mt-1 {{ showStaffId }}

                  //- visiting panel note
                  div.d-flex.align-start
                    div
                      div.yz-medium-body-xs.text-neutral-500 Panel note
                      div.yz-regular-body-sm.neutral700--text.mt-1(v-if="showVisitPanelNote") {{ showVisitPanelNote }}
                    v-spacer
                    yz-btn(v-if="canEditPanelNote" label="Edit" outlined size="sm" @click="showEditPanelNoteDialog = true")

              //- panel claim info
              yz-card.pa-3.mt-2(v-if="transaction && transaction.panel" color="neutral-200")
                div.d-flex(v-if="transaction.panel_claim")
                  div
                    div.yz-medium-body-sm.text-neutral-700 Claim {{ `#${transaction.panel_claim.rid}`}}
                    div.yz-regular-body-xs.text-neutral-500 {{ getClaimDate(transaction) }}
                  v-spacer
                  yz-badge.ml-2(:color="getBadgeColor(transaction.claim_status)" :text-color="getBadgeTextColor(transaction.claim_status)" size="sm")
                    yz-dot(:color="getBadgeTextColor(transaction.claim_status)" size="6px")
                    span.yz-regular-body-sm.ml-2 {{ transaction.claim_status | capitalize }}: {{ getClaimAmount(transaction) }}
                div.d-flex(v-else)
                  div
                    div.yz-medium-body-sm.text-neutral-700 Invoice {{ `#${invoice.rid}`}}
                    div.yz-regular-body-xs.text-neutral-500 {{ transaction.submission_due | displayDate }}
                  v-spacer
                  yz-badge.ml-2(color="neutral-300" text-color="neutral-700" size="sm")
                    yz-dot(color="neutral-500" size="6px")
                    span.yz-regular-body-sm.ml-2 Pending


          v-col(cols="7")
            .right-panel
              //- amount due
              yz-card.pa-3.mb-3(color="neutral-200" border-color="neutral-300" outlined)
                div.d-flex.align-center
                  div
                    div.yz-regular-body-xs.text-neutral-500 Invoice total
                    div.yz-regular-body-xs.text-neutral-500 Paid amount
                  div.ml-3
                    div.yz-medium-body-xs.text-neutral-700 {{ invoice.total_amount | displayPrice($currency) }}
                    div.yz-medium-body-xs.text-neutral-700 {{ totalPaid | displayPrice($currency) }}
                  v-spacer
                  div
                    div.yz-medium-body-sm.text-neutral-700 Amount due
                    div.yz-heading-6.text-red-500 {{ totalBalance | displayPrice($currency) }}
              //- payment methods
              template(v-if="transaction && transaction.panel_claim")
                yz-card.pa-3(border-color="neutral-300" outlined)
                  div.yz-medium-body-xs.text-neutral-500 Payment summary
                  //-  panel
                  div.yz-medium-body-sm.text-neutral-700.mt-3 Panel
                  div.yz-regular-body-sm.text-neutral-700.mt-1 {{ transaction.panel.name }}
                  //- amount
                  div.yz-medium-body-sm.text-neutral-700.mt-3 Amount
                  div.yz-regular-body-sm.text-neutral-700.mt-1 {{ transaction.amount | displayPrice($currency) }}
                  //- payment notes
                  div.yz-medium-body-sm.text-neutral-700.mt-3 Payment notes
                  div.yz-regular-body-sm.text-neutral-700.mt-1(v-if="notes") {{ notes }}
                  div.yz-regular-body-sm.text-neutral-500.italic.mt-1(v-else) No payment notes
                  //- photos
                  div.mt-3(v-if="databinPhotos.length || photos.length")
                    template(v-for="photo in databinPhotos")
                      v-card.round-xl.pa-2.mb-2(outlined)
                        div.d-flex.align-center
                          div.mr-4
                            v-img.round-md(width="40" height="40" :src="$options.filters.smallImage(photo.file)" cover)
                          div.text-sm.neutral700--text {{ photo.filename }}
                          v-spacer
                          y-button(outlined height="24" color="neutral400" background-color="var(--v-neutral100)" prepend-icon="$yzi-trash-03" icon-color="neutral700" icon-size="16" unset-min-width size="xxs" button-class="px-1" :loading="isDeleting" @click="deleteImage(photo)")
                          y-button.ml-2(label="View" outlined height="24" color="neutral400" background-color="var(--v-neutral100)" text-color="neutral700" unset-min-width size="xxs" button-class="px-2" @click="viewImage(photo.file)")
                    template(v-for="photo in photos")
                      v-card.d-flex.pa-2.round-xl.mb-2(outlined)
                        .flex-grow-0.mr-4
                          v-img.round-md.mr-2(width="40" height="40" :src="require('@/assets/image_placeholder.png')" cover)
                        .text-sm.flex-grow-1
                          .font-medium {{ photo.file.name }}
                          .neutral500--text {{ photo.file.size | bytesToSize }} - {{ photo.progress }}% {{ $t('uploaded') }}
                        v-progress-circular(size="22" width="3" :value="photo.progress" color="#0D6EFF")

              template(v-else)
                v-radio-group.my-0(v-model="paymentMethod" row hide-details :disabled="Boolean(transaction)")
                  v-radio(value="self-pay")
                    template(#label)
                      div.text-sm.font-normal.neutral700--text Self-pay
                  v-radio(value="panel")
                    template(#label)
                      div.text-sm.font-normal.neutral700--text Panel
                div.mt-2
                  panel-list-dropdown(v-if="paymentMethod === 'panel'" v-model="selectedPanel" :items="panels" placeholder="Choose panel" dense)
                  y-select.text-sm(v-else v-model="selectedSelfpay" placeholder="Choose payment method" :items="selfpays" item-text="name" item-value="id" small)
                //- payment date
                template(v-if="transaction && transaction.paid_datetime")
                  .text-xs.font-medium.mt-3 Payment date
                  v-text-field.text-sm.round-xl.mt-2(v-model="date" placeholder="DD/MM/YYYY" background-color="var(--v-neutral300)" hide-details outlined dense readonly)
                    template(v-slot:append)
                      eva-icon(name="calendar-outline" fill="#848484")
                //- amount
                .text-xs.font-medium.mt-3 Amount
                div.d-flex.align-end
                  v-text-field.text-sm.round-xl.mt-2(v-model="amount" placeholder="0" outlined dense hide-details :prefix="$currency.symbol" type="number" :rules="rules.has_price")
                  v-card.round-lg.calculator.neutral300.px-2.ml-2(v-if="paymentMethod === 'self-pay'" outlined link height="40" @click="showCalculator = true")
                    v-icon(color="neutral700" size="24") $yzi-calculator
                //- cash balance calculator
                template(v-if="paymentMethod === 'self-pay'")
                  v-card.round-xl.neutral200.pa-4.mt-2(v-if="showCalculator" flat)
                    div.d-flex
                      div
                        div.text-md.font-medium.neutral700--text Cash balance calculator
                        div.text-xs.font-normal.neutral700--text This calculator is only to calculate patient’s balance. It does not affect any accounting aspect of the payment.
                      v-btn(icon x-small @click="showCalculator = false")
                        v-icon(size="20" color="neutral700") mdi-close
                    div.text-xs.font-medium.mt-3 Amount received
                    v-text-field.text-sm.round-xl.mt-2(v-model="amountReceived" placeholder="0" outlined dense hide-details :prefix="$currency.symbol" background-color="neutral100" type="number")
                    div.d-flex.align-center.mt-3
                      div.text-xs.font-medium.neutral700--text Amount due
                      v-spacer
                      div.text-md.font-medium.neutral700--text {{ Math.round(Number(amount) * $currency.multiplier) | displayPrice($currency) }}
                    div.d-flex.align-center.mt-1
                      div.text-xs.font-medium.neutral700--text Customer balance
                      v-spacer
                      div.text-lg.font-medium(:class="customerBalance < 0 ? 'danger500--text' : 'success500--text'") {{ customerBalance | displayPrice($currency) }}
                //- payment notes
                .text-xs.font-medium.mt-3 Payment notes
                v-textarea.text-sm.round-xl.mt-2(v-model="notes" placeholder="Enter your payment notes here..." outlined hide-details rows="3" auto-grow)
                //- photos
                div.mt-3(v-if="databinPhotos.length || photos.length")
                  template(v-for="photo in databinPhotos")
                    v-card.round-xl.pa-2.mb-2(outlined)
                      div.d-flex.align-center
                        div.mr-4
                          v-img.round-md(width="40" height="40" :src="$options.filters.smallImage(photo.file)" cover)
                        div.text-sm.neutral700--text {{ photo.filename }}
                        v-spacer
                        y-button(outlined height="24" color="neutral400" background-color="var(--v-neutral100)" prepend-icon="$yzi-trash-03" icon-color="neutral700" icon-size="16" unset-min-width size="xxs" button-class="px-1" :loading="isDeleting" @click="deleteImage(photo)")
                        y-button.ml-2(label="View" outlined height="24" color="neutral400" background-color="var(--v-neutral100)" text-color="neutral700" unset-min-width size="xxs" button-class="px-2" @click="viewImage(photo.file)")
                  template(v-for="photo in photos")
                    v-card.d-flex.pa-2.round-xl.mb-2(outlined)
                      .flex-grow-0.mr-4
                        v-img.round-md.mr-2(width="40" height="40" :src="require('@/assets/image_placeholder.png')" cover)
                      .text-sm.flex-grow-1
                        .font-medium {{ photo.file.name }}
                        .neutral500--text {{ photo.file.size | bytesToSize }} - {{ photo.progress }}% {{ $t('uploaded') }}
                      v-progress-circular(size="22" width="3" :value="photo.progress" color="#0D6EFF")

        //- upload photo
        .text-xs.font-medium.mt-3 {{ $t('upload_photo') }}
        div.mt-2
          v-row(align="center")
            v-col.grow
              yz-card.py-3(outlined border-style="dashed" border-color="neutral-400")
                .text-center
                  v-avatar.mb-2(color="neutral300" size="36")
                    v-icon(color="neutral500" size="18") $yzi-image-01
                  div.text-xs
                    span.info500--text.font-semibold {{ $t('click_to_upload') }}
                    span.neutral700--text {{ $t('or_drag_and_drop') }}
                  .text-xxs.neutral500--text {{ $t('photo_upload_limitation') }}
                input.input-change-layout(ref="file" type="file" @change="onFileChange" accept="image/*")
            v-col.shrink
              v-card.round-lg(outlined)
                vue-qrcode.mb-n1(v-if="databinKey" :value="getQRLinkURL()" :options="{ width: 100 }")
            v-col(cols="4")
              .text-sm.font-semibold {{ $t('upload_photo_from_other_device') }}
              .mt-1 {{ $t('scan_qr_code_below_to_upload') }}

    v-divider
    v-card-actions.py-3.px-6
      y-button(label="Cancel" underline size="sm" unset-min-width button-class="px-0" @click="closeDialog")
      v-spacer
      y-button(label="Save" size="sm" unset-min-width button-class="px-3" :disabled="!selectedPayment" :loading="isLoading" @click="recordPayment")
    
    .dialogs
      view-image-dialog(v-if="showViewImageDialog" :open.sync="showViewImageDialog" :image="selectedFile" @closed="selectedFile = null")
      edit-panel-note-dialog(v-if="showEditPanelNoteDialog" :open.sync="showEditPanelNoteDialog" :registration="registration" :amount="amount" @updated="handlePanelNoteUpdated")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { resizeImage } from '@/utils'
import { isFileSizeOverLimit } from '@/utils'
import PanelListDropdown from '@/components/clinic/custom/PanelListDropdown.vue'
import ViewImageDialog from '@/components/dialogs/ViewImageDialog.vue'
import EditPanelNoteDialog from '@/components/clinic/checkout/EditPanelNoteDialog.vue'
import VueQrcode from '@chenfengyuan/vue-qrcode'

export default {
  components: { PanelListDropdown, ViewImageDialog, EditPanelNoteDialog, VueQrcode },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    transaction: {
      type: Object,
      default: null
    },
    panel: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      isFetchingPaymentMethods: false,
      isFetchingClaimPanels: false,
      isLoading: false,
      isDeleting: false,
      showViewImageDialog: false,
      showEditPanelNoteDialog: false,
      showCalculator: false,
      paymentMethod: 'self-pay',
      amount: null,
      amountReceived: null,
      date: null,
      notes: null,
      selectedPanel: null,
      selectedSelfpay: null,
      polling: null,
      selectedFile: null,
      panels: [],
      selfpays: [],
      photos: [],
      databinPhotos: [],
      claimPanels: [],
      rules: {
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 0.01 || `Amount should be more than ${this.$currency.symbol} 0`]
      }
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      invoice: 'treatment/getInvoice',
      registration: 'treatment/getRegistration',
      transactions: 'treatment/getTransactions',
      databinKey: 'clinic/getDatabinKey',
      hqSelectedCompany: 'hq/getSelectedCompany',
      isHq: 'hq/getIsHq'
    }),
    showDialog: {
      get () {
        return this.open 
      },
      set (newValue) {
        this.$emit('update:open', newValue)
      }
    },
    selectedPayment () {
      return this.paymentMethod === 'panel' ? this.panels.find(i => i.id === this.selectedPanel) : this.selfpays.find(i => i.id === this.selectedSelfpay)
    },
    totalPaid () {
      if (!this.invoice.pid) {
        return 0
      }
      if (this.transactions.length === 0) {
        return 0
      }
      return this.transactions.reduce((sum, trans) => {
        sum += trans.amount
        return sum
      }, 0)
    },
    totalBalance () {
      if (!this.invoice.pid) {
        return 0
      }
      return this.invoice.total_amount - this.totalPaid
    },
    showRemarks () {
      return this.selectedPayment?.channel === 'panel' && this.selectedPayment?.display_remarks && this.selectedPayment?.remarks
    },
    showVerifyUrl () {
      return this.selectedPayment?.channel === 'panel' && !this.selectedPayment?.display_remarks && this.selectedPayment?.verification_link
    },
    showVisitPanelNote () {
      if (this.selectedPayment?.channel !== 'panel' || !this.selectedPanel) {
        return false
      }
      if (this.transaction && !this.transaction.panel) {
        return this.registration?.default_payment?.note
      }
      let note = null
      if (this.selectedPanel === this.registration?.default_payment?.panel?.id) {
        note = this.registration?.default_payment?.note
      } else if (this.claimPanels.find(p => p.panel.payment_method === this.selectedPanel)) {
        note = this.claimPanels.find(p => p.panel.payment_method === this.selectedPanel).last_visit_note
      }
      return note
    },
    showStaffId () {
      if (this.transaction && !this.transaction.panel) {
        return this.registration?.default_payment?.staff_id
      }
      let staffId = null
      if (this.selectedPanel === this.registration?.default_payment?.panel?.id) {
        staffId = this.registration?.default_payment?.staff_id
      } else if (this.claimPanels.find(p => p.panel.payment_method === this.selectedPanel)) {
        staffId = this.claimPanels.find(p => p.panel.payment_method === this.selectedPanel).staff_id
      }
      return staffId
    },
    canEditPanelNote () {
      return this.selectedPanel === this.registration?.default_payment?.panel?.id
    },
    customerBalance () {
      let balance = Math.round(Number(this.amount) * this.$currency.multiplier) || 0
      if (this.amountReceived) {
        return Math.round(Number(this.amountReceived) * this.$currency.multiplier) - balance
      } else {
        balance = -balance
      }
      return balance
    }
  },
  mounted () {
    this.fetchPaymentMethods()
    this.fetchClaimPanels()
    this.createDatabin()
  },
  methods: {
    ...mapActions(['showMessage']),
    setData () {
      if (this.transaction) {
        this.amount = Number(this.transaction.amount) / this.$currency.multiplier
        this.date = this.transaction.paid_datetime ? this.$options.filters.humanizeDate(this.transaction.paid_datetime) : null
        this.notes = this.transaction.internal_description
        this.paymentMethod = this.transaction.channel
        if (this.transaction.channel === 'panel') {
          this.selectedPanel = Number(this.transaction.ref_id)
        } else {
          this.selectedSelfpay = Number(this.transaction.ref_id)
        }
        if (this.transaction.documents.length) {
          this.databinPhotos = [...this.transaction.documents]
          this.updateDatabin()
        }
      } else if (this.panel) {
        this.paymentMethod = 'panel'
        this.selectedPanel = Number(this.panel.id)
      }
    },
    fetchPaymentMethods () {
      this.isFetchingPaymentMethods = true
      let params = {
        company: this.isHq ? this.hqSelectedCompany.id : this.company.id,
        enabled: true,
        fields: 'id,name,channel,verification_link,remarks,display_remarks,panel',
        page_size: -1
      }
      this.$axios.get('/payment_methods', { params })
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.panels = resp.data.results.filter(panel => panel.channel === 'panel')
            this.selfpays = resp.data.results.filter(panel => panel.channel === 'self-pay')
          }
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingPaymentMethods = false
        })
    },
    fetchClaimPanels () {
      this.isFetchingClaimPanels = true
      let params = { fields: 'panels' }
      this.$axios.get(`/clinics/${this.company.slug}/patients/${this.invoice.customer.id}`, { params })
        .then(resp => {
          this.claimPanels = resp.data.panels || []
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingClaimPanels = false
        })
    },
    createDatabin () {
      this.$axios.post('/databin/?ttl=900')
        .then(resp => {
          this.$store.commit('clinic/setDatabinKey', resp.data.key)
          this.startPolling()
          this.setData()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    refreshDatabin () {
      this.$axios.get(`/databin/${this.databinKey}`)
        .then(resp => {
          if (JSON.stringify(resp.data) !== '{}') {
            this.databinPhotos = resp.data
          }
        })
        .catch(() => {
        })
        .finally(() => {
        })
    },
    uploadToDatabin (photo) {
      const data = [...this.databinPhotos, photo]
      if (this.databinKey) {
        this.$axios.put(`/databin/${this.databinKey}/?ttl=900`, data)
          .then(resp => {
            this.databinPhotos = resp.data.data
            this.photos.pop(photo)
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
      }
    },
    updateDatabin () {
      const data = [...this.databinPhotos]
      if (this.databinKey) {
        this.$axios.put(`/databin/${this.databinKey}/?ttl=900`, data)
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
      }
    },
    async onFileChange(e) {
      const files = e.target.files || e.dataTransfer.files
      if (!files.length) {
        return
      }
      if (isFileSizeOverLimit(files[0], 5)) {
        this.showMessage(['error', this.$t('error_message.file_reached_limit')])
        return
      }
      const resizedImage = await resizeImage(files[0], `photo_${Date.now()}.jpeg`)
      const photo = {
        data: resizedImage.dataURL,
        file: resizedImage.file,
        progress: 0
      }
      this.photos.push(photo)
      this.uploadPhoto(photo)
    },
    uploadPhoto (photo) {
      const formData = new FormData()
      formData.append('file', photo.file)
      this.$axios.post(`/documents/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: progressEvent => (photo.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total))
        })
        .then(resp => {
          const uploadedPhoto = resp.data
          this.uploadToDatabin(uploadedPhoto)
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    recordPayment () {
      if (!this.$refs.form.validate()) {
        this.showMessage(['error', this.$t('error_message.please_fill_out_all_required')])
        return
      }
      this.isLoading = true
      const amountInCent = Math.round(Number(this.amount) * this.$currency.multiplier)
      if (this.transaction) {
        let payload = {
          amount: amountInCent,
          channel: this.paymentMethod,
          ref_id: this.selectedPayment?.id,
          payment_method: this.selectedPayment?.name,
          internal_description: this.notes,
        }
        if (this.paymentMethod === 'panel') {
          payload.panel = this.panels.find(panel => panel.id === this.selectedPanel)?.panel?.id ?? null
        }
        if (this.databinPhotos.length) {
          payload.documents = this.databinPhotos.map(photo => photo.hid)
        }
        const endpoint = `/transactions/${this.transaction.uid}`
        this.axios.patch(endpoint, payload)
          .then(resp => {
            let activity = `Edit payment (#${this.invoice.rid})`
            this.updateOldActivityLog(endpoint, payload, resp.data, 'PATCH', this.transaction.uid, activity)
            const amountDisplay = this.$options.filters.displayPrice(amountInCent, this.$currency)
            this.createActivityLog('edit_transaction', `${this.selectedPayment?.name || 'Payment'} was edited to amount ${amountDisplay}`)
            this.$emit('updated')
            this.closeDialog()
          })
          .catch((err) => {
            let message = this.$t('error_message.unable_to_complete_request')   
            if (err.response.data) {
              if (err.response.data.detail) {
                message = err.response.data.detail
              } else {
                let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
                if (data) { message = data }
              }
            }
            this.showMessage(['error', message])
          })
          .finally(() => {
            this.isLoading = false
          })
      } else {
        let payload = {
          amount: amountInCent,
          gateway: 'direct',
          lead: this.invoice.lead,
          paid: this.paymentMethod === 'self-pay',
          channel: this.paymentMethod,
          ref_id: this.selectedPayment?.id,
          payment_method: this.selectedPayment?.name,
          internal_description: this.notes,
        }
        if (this.paymentMethod === 'panel') {
          payload.panel = this.panels.find(panel => panel.id === this.selectedPanel)?.panel?.id ?? null
        }
        const endpoint = `/transactions/`
        this.axios.post(endpoint, payload)
          .then(resp => {
            let activity = `Add payment (#${this.invoice.rid})`
            this.updateOldActivityLog(endpoint, payload, resp.data, 'POST', null, activity)
            const amountDisplay = this.$options.filters.displayPrice(amountInCent, this.$currency)
            this.createActivityLog('add_transaction', `${this.selectedPayment?.name || 'Payment'} amount ${amountDisplay} was recorded`)
            this.checkUpdatePhotos(resp.data)
          })
          .catch((err) => {
            let message = this.$t('error_message.unable_to_complete_request')   
            if (err.response.data) {
              if (err.response.data.detail) {
                message = err.response.data.detail
              } else {
                let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
                if (data) { message = data }
              }
            }
            this.showMessage(['error', message])
            this.isLoading = false
          })
      }
      // Open cash drawer for cash payment method
      const cashPaymentMethod = this.selfpays.find(method => method.name.toLowerCase() === 'cash') || null
      const isSelfPay = this.paymentMethod === 'self-pay'
      if (isSelfPay && cashPaymentMethod && this.selectedSelfpay === cashPaymentMethod.id) {
        const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
        this.axios.post(`/clinics/${companySlug}/open_cash_drawer`)
          .then(() => {
            // this.showMessage(['success', 'Cash drawer opened'])
          })
          .catch(() => {
            // this.showMessage(['error', 'Failed to open cash drawer'])
          })
      }
    },
    checkUpdatePhotos (data) {
      if (this.databinPhotos.length) {
        let payload = {
          documents: this.databinPhotos.map(photo => photo.hid)
        }
        const endpoint = `/transactions/${data.uid}`
        this.axios.patch(endpoint, payload)
          .then(resp => {
            let activity = `Update photos (${data.uid})`
            this.updateOldActivityLog(endpoint, payload, resp.data, 'PATCH', data.uid, activity)
            this.$emit('updated')
            this.closeDialog()
          })
          .catch((err) => {
            let message = this.$t('error_message.unable_to_complete_request')   
            if (err.response.data) {
              if (err.response.data.detail) {
                message = err.response.data.detail
              } else {
                let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
                if (data) { message = data }
              }
            }
            this.showMessage(['error', message])
          })
          .finally(() => {
            this.isLoading = false
          })
      } else {
        this.$emit('updated')
        this.closeDialog()
      }
    },
    getQRLinkURL () {
      return encodeURI(`https://${process.env.VUE_APP_BASE_DOMAIN}/upload-photo/${this.databinKey}`)
    },
    startPolling () {
      this.stopPolling()
      this.polling = setInterval(() => {
        this.refreshDatabin()
      }, 4000)
    },
    stopPolling () {
      clearInterval(this.polling)
    },
    viewImage (image) {
      this.selectedFile = image
      this.showViewImageDialog = true
    },
    deleteImage (photo) {
      if (this.transaction) {
        this.isDeleting = true
        this.stopPolling()
        this.$axios.delete(`/documents/${photo.hid}`)
          .then(() => {
            this.databinPhotos = this.databinPhotos.filter(p => p.hid !== photo.hid)
            this.updateDatabin()
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
          .finally(() => {
            this.startPolling()
            this.isDeleting = false
          })
      } else {
        this.databinPhotos = this.databinPhotos.filter(p => p.hid !== photo.hid)
        this.updateDatabin()
      }
    },
    copyText(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.showMessage(['success', 'Copied to clipboard'])
      }).catch(err => {
        console.error('Failed to copy text: ', err)
        this.showMessage(['error', 'Failed to copy text'])
      })
    },
    closeDialog () {
      this.$emit('closed')
      this.showDialog = false
    },
    handlePanelNoteUpdated (note) {
      this.$store.commit('treatment/updateRegistration', {
        default_payment: {
          ...this.registration.default_payment,
          note: note
        }
      })
    },
    updateOldActivityLog (endpoint, payload, response, method, id, activity) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'checkout',
        activity: activity,
        raw_data: { endpoint, payload, response, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
    createActivityLog (tag, activity) {
      const payload = {
        company: this.company.id,
        app_model: 'form.lead',
        object_id: this.$route.params.pid,
        tag,
        group: 'checkout',
        activity
      }
      this.$axios.post('/actions', payload).catch(() => {})
    },
    getClaimAmount(claim) {
      if (claim.claim_status === 'cancelled') {
        return null
      }
      if (claim.claim_status === 'rejected') {
        return this.$options.filters.displayPrice(claim.rejected_amount || 0)
      }
      return this.$options.filters.displayPrice(claim.amount || 0)
    },
    getClaimDate(claim) {
      if (claim.claim_status === 'cancelled') {
        return null
      }
      return this.$options.filters.displayDate(claim.panel_claim[`${claim.claim_status}_date`] || null)
    },
    getBadgeTextColor(status) {
      switch (status) {
        case 'cancelled':
          return 'red-500'
        case 'approved':
          return 'blue-500'
        case 'rejected':
          return 'red-500'
        case 'submitted':
          return 'yellow-500'
        case 'received':
          return 'green-500'
        default:
          return 'neutral-700'
      }
    },
    getBadgeColor(status) {
      switch (status) {
        case 'cancelled':
          return 'red-100'
        case 'approved':
          return 'blue-100'
        case 'rejected':
          return 'red-100'
        case 'submitted':
          return 'yellow-100'
        case 'received':
          return 'green-100'
        default:
          return 'neutral-300'
      }
    },
    openVerificationLink (url) {
      window.open(url, '_blank')
    }
  },
  destroyed () {
    this.stopPolling()
  }
}
</script>

<style scoped>
.input-change-layout {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  opacity: 0;
}
.calculator {
  border-color: var(--v-neutral400) !important;
  align-content: center;
}
</style>